/**
 * Platformer Game
 * Jump platforms and collect coins
 */

import React, { useState, useEffect, useRef } from 'react';
import { useLocalStorage } from './hooks/useLocalStorage';
import GameLayout from './common/GameLayout';
import ScoreDisplay from './common/ScoreDisplay';
import { GAME_CONFIG } from './config/gameConfig';

const Platformer = ({ onHome }) => {
  const canvasRef = useRef(null);
  const [gameState, setGameState] = useState(GAME_CONFIG.states.MENU);
  const [score, setScore] = useLocalStorage('platformer_score', 0);
  const [highScore, setHighScore] = useLocalStorage('platformer_highScore', 0);

  const gameStateRef = useRef({
    player: { x: 50, y: 300, width: 20, height: 20, velocityY: 0, isJumping: false },
    platforms: [],
    coins: [],
    enemies: [],
    level: 1,
    coinsCollected: 0,
  });

  const CANVAS_WIDTH = 600;
  const CANVAS_HEIGHT = 400;
  const GRAVITY = 0.5;
  const JUMP_STRENGTH = -12;

  const generateLevel = (levelNum) => {
    const platforms = [];
    const baseHeight = CANVAS_HEIGHT - 50;

    // Ground
    platforms.push({ x: 0, y: baseHeight, width: CANVAS_WIDTH, height: 50 });

    // Level-specific platforms
    if (levelNum === 1) {
      platforms.push({ x: 150, y: 300, width: 100, height: 15 });
      platforms.push({ x: 350, y: 250, width: 100, height: 15 });
      platforms.push({ x: 100, y: 180, width: 80, height: 15 });
      platforms.push({ x: 450, y: 200, width: 100, height: 15 });
    } else if (levelNum === 2) {
      platforms.push({ x: 120, y: 320, width: 80, height: 15 });
      platforms.push({ x: 280, y: 280, width: 80, height: 15 });
      platforms.push({ x: 440, y: 240, width: 80, height: 15 });
      platforms.push({ x: 200, y: 160, width: 60, height: 15 });
    } else {
      for (let i = 0; i < 6; i++) {
        platforms.push({
          x: Math.random() * (CANVAS_WIDTH - 80),
          y: 350 - i * 60,
          width: 80,
          height: 15,
        });
      }
    }

    return platforms;
  };

  const generateCoins = (platformList) => {
    const coins = [];
    platformList.slice(1).forEach((platform) => {
      coins.push({
        x: platform.x + platform.width / 2,
        y: platform.y - 30,
        radius: 8,
        collected: false,
      });
    });
    return coins;
  };

  const startGame = () => {
    const platforms = generateLevel(1);
    const coins = generateCoins(platforms);

    gameStateRef.current = {
      player: { x: 50, y: 300, width: 20, height: 20, velocityY: 0, isJumping: false },
      platforms,
      coins,
      enemies: [],
      level: 1,
      coinsCollected: 0,
    };
    setScore(0);
    setGameState(GAME_CONFIG.states.PLAYING);
  };

  const resetGame = () => {
    if (score > highScore) setHighScore(score);
    setGameState(GAME_CONFIG.states.MENU);
  };

  useEffect(() => {
    if (gameState !== GAME_CONFIG.states.PLAYING) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const game = gameStateRef.current;

    const keysPressed = {};

    const handleKeyDown = (e) => {
      keysPressed[e.key] = true;
      if ((e.key === ' ' || e.key === 'ArrowUp') && !game.player.isJumping) {
        game.player.velocityY = JUMP_STRENGTH;
        game.player.isJumping = true;
        e.preventDefault();
      }
    };

    const handleKeyUp = (e) => {
      keysPressed[e.key] = false;
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    const gameLoop = setInterval(() => {
      // Clear canvas
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

      // Player movement
      if (keysPressed['ArrowLeft'] || keysPressed['a']) {
        game.player.x -= 5;
      }
      if (keysPressed['ArrowRight'] || keysPressed['d']) {
        game.player.x += 5;
      }

      // Boundary check
      game.player.x = Math.max(0, Math.min(game.player.x, CANVAS_WIDTH - game.player.width));

      // Apply gravity
      game.player.velocityY += GRAVITY;
      game.player.y += game.player.velocityY;

      // Platform collision
      let onGround = false;
      game.platforms.forEach((platform) => {
        if (
          game.player.x + game.player.width > platform.x &&
          game.player.x < platform.x + platform.width &&
          game.player.y + game.player.height >= platform.y &&
          game.player.y + game.player.height <= platform.y + platform.height + 10 &&
          game.player.velocityY >= 0
        ) {
          game.player.y = platform.y - game.player.height;
          game.player.velocityY = 0;
          game.player.isJumping = false;
          onGround = true;
        }
      });

      // Fall off screen
      if (game.player.y > CANVAS_HEIGHT) {
        setGameState(GAME_CONFIG.states.GAME_OVER);
        if (score > highScore) setHighScore(score);
      }

      // Coin collection
      game.coins.forEach((coin) => {
        if (!coin.collected) {
          const dx = game.player.x + game.player.width / 2 - coin.x;
          const dy = game.player.y + game.player.height / 2 - coin.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance < coin.radius + 15) {
            coin.collected = true;
            game.coinsCollected++;
            setScore((prev) => prev + 50);
          }
        }
      });

      // Level progression
      if (game.coinsCollected > game.level * 3) {
        game.level++;
        const newPlatforms = generateLevel(game.level);
        const newCoins = generateCoins(newPlatforms);
        game.platforms = newPlatforms;
        game.coins = newCoins;
        game.coinsCollected = 0;
        game.player.y = CANVAS_HEIGHT - 100;
        setScore((prev) => prev + 100);
      }

      // Draw platforms
      ctx.fillStyle = GAME_CONFIG.colors.primary;
      game.platforms.forEach((platform) => {
        ctx.fillRect(platform.x, platform.y, platform.width, platform.height);
      });

      // Draw coins
      ctx.fillStyle = GAME_CONFIG.colors.accent2;
      game.coins.forEach((coin) => {
        if (!coin.collected) {
          ctx.beginPath();
          ctx.arc(coin.x, coin.y, coin.radius, 0, Math.PI * 2);
          ctx.fill();
        }
      });

      // Draw player
      ctx.fillStyle = GAME_CONFIG.colors.accent;
      ctx.fillRect(game.player.x, game.player.y, game.player.width, game.player.height);

      // Draw HUD
      ctx.fillStyle = GAME_CONFIG.colors.light;
      ctx.font = '14px Arial';
      ctx.fillText(`Level: ${game.level}`, 10, 20);
      ctx.fillText(`Coins: ${game.coinsCollected}`, 10, 40);
    }, 30);

    return () => {
      clearInterval(gameLoop);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [gameState, score, highScore]);

  return (
    <GameLayout
      title="Platformer 🎮"
      score={score}
      highScore={highScore}
      gameState={gameState}
      onRestart={resetGame}
      onHome={onHome}
    >
      <div style={styles.gameArea}>
        {gameState === GAME_CONFIG.states.MENU && (
          <div style={styles.menuContainer}>
            <h2 style={styles.menuTitle}>Platformer</h2>
            <p style={styles.menuDescription}>
              Jump platforms and collect coins! Use Arrow Keys or WASD to move, Space to jump.
            </p>
            <button
              onClick={startGame}
              className="btn btn-primary btn-lg"
              style={{ marginTop: '1rem' }}
            >
              🎮 Start Game
            </button>
          </div>
        )}

        {gameState === GAME_CONFIG.states.PLAYING && (
          <>
            <canvas
              ref={canvasRef}
              width={CANVAS_WIDTH}
              height={CANVAS_HEIGHT}
              style={styles.canvas}
            />
            <div style={styles.controls}>Arrow Keys/WASD: Move | Space: Jump</div>
          </>
        )}

        {gameState === GAME_CONFIG.states.GAME_OVER && (
          <div style={styles.gameOverCard}>
            <h1 style={styles.gameOverTitle}>Game Over</h1>
            <ScoreDisplay score={score} stats={{ 'Final Score': score }} />
            <button
              onClick={startGame}
              className="btn btn-primary mt-3"
              style={{ width: '100%' }}
            >
              🔄 Play Again
            </button>
          </div>
        )}
      </div>
    </GameLayout>
  );
};

const styles = {
  gameArea: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1.5rem',
    width: '100%',
  },
  menuContainer: {
    backgroundColor: `rgba(0, 0, 0, 0.5)`,
    border: `2px solid ${GAME_CONFIG.colors.primary}`,
    borderRadius: GAME_CONFIG.radius.lg,
    padding: '3rem 2rem',
    textAlign: 'center',
  },
  menuTitle: {
    color: GAME_CONFIG.colors.accent,
    fontSize: '2rem',
    marginBottom: '1rem',
    fontWeight: 'bold',
  },
  menuDescription: {
    color: GAME_CONFIG.colors.light,
  },
  canvas: {
    border: `2px solid ${GAME_CONFIG.colors.primary}`,
    borderRadius: '4px',
    backgroundColor: '#000',
    display: 'block',
  },
  controls: {
    color: GAME_CONFIG.colors.light,
    fontSize: '0.9rem',
    backgroundColor: `rgba(0, 212, 255, 0.1)`,
    border: `1px solid ${GAME_CONFIG.colors.accent}`,
    borderRadius: GAME_CONFIG.radius.md,
    padding: '1rem',
  },
  gameOverCard: {
    backgroundColor: `rgba(0, 0, 0, 0.5)`,
    border: `2px solid ${GAME_CONFIG.colors.accent}`,
    borderRadius: GAME_CONFIG.radius.lg,
    padding: '2rem',
    textAlign: 'center',
    maxWidth: '400px',
  },
  gameOverTitle: {
    color: GAME_CONFIG.colors.danger,
    marginBottom: '1rem',
    fontSize: '2rem',
  },
};

export default Platformer;
